import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ExternalLink } from "lucide-react";
import { adminListProvenanceDetectors, adminDetectProvenance } from "../../lib/api";

const OUTCOME_COLOR = {
  detected: "bg-red-100 text-red-800",
  not_detected: "bg-emerald-100 text-emerald-800",
  unsupported: "bg-zinc-200 text-zinc-800",
  access_pending: "bg-amber-100 text-amber-800",
  error: "bg-red-100 text-red-800",
  disabled: "bg-zinc-200 text-zinc-800",
};

export default function ProvenancePanel() {
  const [detectors, setDetectors] = useState([]);
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    adminListProvenanceDetectors().then((d) => setDetectors(d.detectors || [])).catch(() => {});
  }, []);

  const run = async () => {
    if (text.trim().length < 20) return toast.error("Paste at least 20 characters of text.");
    setRunning(true);
    try {
      const r = await adminDetectProvenance(text, null);
      setResult(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Detection failed"); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="admin-provenance" className="space-y-6">
      {/* Registered detectors */}
      <div className="border border-border bg-card p-4" data-testid="provenance-detectors">
        <p className="overline text-oxford mb-2">Registered detectors</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {detectors.map((d) => (
            <div key={d.provider_key} className={`border ${d.ready ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"} p-3`} data-testid={`provenance-detector-${d.provider_key}`}>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={14} className={d.ready ? "text-emerald-600" : "text-amber-600"} />
                <span className="text-sm font-semibold">{d.provider}</span>
              </div>
              <p className={`text-[10px] font-mono uppercase tracking-widest ${d.ready ? "text-emerald-700" : "text-amber-700"}`}>{d.ready ? "Ready" : "Access pending"}</p>
              {!d.ready && d.provider_key === "anthropic" && (
                <p className="text-[11px] text-zinc-600 mt-1">Application draft: <code className="bg-white px-1">/app/memory/anthropic_watermark_application.md</code></p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Wording safeguards */}
      <div className="border-2 border-indigo-300 bg-indigo-50 p-4" data-testid="provenance-wording">
        <p className="overline text-oxford mb-2">Student-facing wording (never overclaim)</p>
        <div className="text-xs text-zinc-700 space-y-1">
          <p><b>Detected:</b> &quot;A detectable signal consistent with {'{provider}'} involvement was found in part of this submission … does not establish authorship or academic misconduct.&quot;</p>
          <p><b>Not detected:</b> &quot;No {'{provider}'} provenance signal detected. This does not establish that {'{provider}'} or another AI system was not used.&quot;</p>
          <p><b>Coming soon (current mode):</b> &quot;MyAE is developing new ways to help you understand whether AI tools may have been involved in your writing …&quot;</p>
        </div>
      </div>

      {/* Admin test detection */}
      <div className="border border-border bg-card p-4" data-testid="provenance-test">
        <p className="overline text-oxford mb-2">Test detection (admin only)</p>
        <p className="text-xs text-muted-foreground mb-2">Paste text below to fan out through every registered detector. Results are admin-only — not shown to students until an official mechanism is validated.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder="Paste essay text…" className="w-full border border-border px-2 py-1.5 text-xs font-mono" data-testid="provenance-test-input" />
        <div className="mt-2">
          <button onClick={run} disabled={running} className="bg-oxford text-white px-3 py-1.5 text-xs hover:bg-[#2C5282] flex items-center gap-1.5" data-testid="provenance-test-run">
            {running ? <Loader2 size={12} className="animate-spin" /> : null} Detect
          </button>
        </div>

        {result && (
          <div className="mt-4 space-y-2" data-testid="provenance-test-result">
            {(result.signals || []).map((s) => (
              <div key={s.provider_key} className="border border-border bg-secondary p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">{s.provider}</span>
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${OUTCOME_COLOR[s.outcome] || "bg-zinc-100"}`}>{s.outcome}</span>
                </div>
                {s.confidence != null && <p className="text-xs">Confidence: <span className="font-mono">{s.confidence}</span></p>}
                {s.detected_fraction != null && <p className="text-xs">Coverage: <span className="font-mono">{s.detected_fraction}</span></p>}
                {s.detail && <p className="text-[11px] text-muted-foreground mt-1">{s.detail}</p>}
                {(s.model_coverage || []).length > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">Coverage: {s.model_coverage.join(", ")}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-card p-4 text-xs text-muted-foreground" data-testid="provenance-references">
        <p className="overline text-oxford mb-1">References</p>
        <ul className="list-disc pl-5 space-y-0.5">
          <li>Anthropic watermark detection API — private preview. Application pack draft: <code>/app/memory/anthropic_watermark_application.md</code></li>
          <li>Model coverage: Claude Fable 5.1, Claude Mythos 5.1 (older models unmarked)</li>
          <li>OpenAI + Google SynthID detectors: placeholders — will activate when providers open access <ExternalLink size={10} className="inline" /></li>
        </ul>
      </div>
    </div>
  );
}
